import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { connection, withRetry } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flame, Zap, Gamepad2, ArrowRight, Clock, CheckCircle2, Brain, Trophy } from "lucide-react";
import sounds from "@/lib/sounds";
import SkillPreview from "@/components/SkillPreview";
import { useAuth } from "@/contexts/AuthContext";

const MASTERY_REQUIRED = 3;

export default function MathPractice() {
  const navigate = useNavigate();
  const { updateUserCoins } = useAuth();
  const [phase, setPhase] = useState("loading");
  const [problem, setProblem] = useState(null);
  const [answer, setAnswer] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(5);
  const [feedback, setFeedback] = useState(null);
  const [progress, setProgress] = useState({ session_correct: 0, session_streak: 0, session_misses: 0, target: 20 });
  const [mastery, setMastery] = useState({ total_facts: 0, mastered_count: 0 });
  const [focusSet, setFocusSet] = useState([]);
  const [focusStreaks, setFocusStreaks] = useState({});
  const [focusMastered, setFocusMastered] = useState([]);
  const [currentFact, setCurrentFact] = useState(null);
  const [bonusMode, setBonusMode] = useState(false);
  const [flashClass, setFlashClass] = useState("");
  const [sessionBand, setSessionBand] = useState(null);
  const [factMasteredFlash, setFactMasteredFlash] = useState(false);
  const [setCompleteFlash, setSetCompleteFlash] = useState(false);

  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const timeoutFired = useRef(false);
  const busyRef = useRef(false);
  const problemIdRef = useRef(null);
  const nextTimerRef = useRef(null);
  const fetchSeq = useRef(0);
  const [offline, setOffline] = useState(!connection.online);

  useEffect(() => {
    const l = (on) => setOffline(!on);
    connection.listeners.add(l);
    return () => connection.listeners.delete(l);
  }, []);
  const prevStreak = useRef(0);

  const startSession = useCallback(async () => {
      try {
        const { data } = await API.post("/practice/start");
        setSessionBand(data.skill_band);
        if (data.mastery_total) setMastery({ total_facts: data.mastery_total, mastered_count: data.mastery_count || 0 });
        if (data.focus_set) setFocusSet(data.focus_set);
        if (data.session_correct === 0) {
          setPhase("preview");
        } else {
          fetchProblem();
        }
      } catch (err) {
        toast.error("Failed to start practice");
        navigate("/student");
      }
  }, []); // eslint-disable-line

  useEffect(() => {
    startSession();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []); // eslint-disable-line

  const fetchProblem = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const { data } = await withRetry(() => API.get("/practice/problem"));
      if (seq !== fetchSeq.current) return;
      if (data.reward_ready) {
        setBonusMode(data.bonus_mode);
        setPhase("reward");
        return;
      }
      if (data.band_complete) {
        setMastery(data.mastery);
        setPhase("band_complete");
        return;
      }
      problemIdRef.current = data.problem_id;
      busyRef.current = false;
      setProblem(data);
      setProgress(data.progress);
      setMastery(data.mastery);
      setFocusSet(data.focus_set || []);
      setFocusStreaks(data.focus_streaks || {});
      setFocusMastered(data.focus_mastered || []);
      setCurrentFact(data.fact_key);
      setMaxTime(data.timer);
      setTimeLeft(data.timer);
      setAnswer("");
      setPhase("active");
      timeoutFired.current = false;
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      toast.error("Failed to load problem");
    }
  }, []);

  // Timer pauses while offline and restarts in full once reconnected
  useEffect(() => {
    if (phase !== "active" || offline) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const start = Date.now();
    const dur = maxTime * 1000;
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const rem = Math.max(0, (dur - elapsed) / 1000);
      setTimeLeft(rem);
      if (rem <= 0 && !timeoutFired.current) {
        timeoutFired.current = true;
        clearInterval(timerRef.current);
        handleTimeout();
      }
    }, 80);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase, maxTime, offline]); // eslint-disable-line

  // Only one "next problem" can ever be pending
  const scheduleNext = (fn, delay) => {
    if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    nextTimerRef.current = setTimeout(() => { nextTimerRef.current = null; fn(); }, delay);
  };

  useEffect(() => () => { if (nextTimerRef.current) clearTimeout(nextTimerRef.current); }, []);

  // Duplicate submit was rejected; the other request normally loads the next problem
  const recoverIfStuck = () => setTimeout(() => {
    if (!nextTimerRef.current && busyRef.current) fetchProblem();
  }, 3000);

  const handleTimeout = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const pid = problemIdRef.current;
      const { data } = await withRetry(() => API.post("/practice/timeout", { problem_id: pid }));
      sounds.timeout();
      setFeedback({ is_correct: false, correct_answer: data.correct_answer, timeout: true });
      setFlashClass("flash-wrong");
      setPhase("feedback");
      scheduleNext(() => { setFlashClass(""); fetchProblem(); }, 2500);
    } catch (err) {
      if (err.response?.status === 409) recoverIfStuck();
      else scheduleNext(fetchProblem, 300);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!answer.trim() || phase !== "active" || busyRef.current) return;
    const numAnswer = parseFloat(answer);
    if (isNaN(numAnswer)) return;
    busyRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    timeoutFired.current = true;
    try {
      const body = { answer: numAnswer, time_taken: maxTime - timeLeft, problem_id: problemIdRef.current };
      const { data } = await withRetry(() => API.post("/practice/answer", body));
      setFeedback(data);
      setProgress(data.progress);
      if (data.mastery) setMastery(data.mastery);
      setFlashClass(data.is_correct ? "flash-correct" : "flash-wrong");

      if (data.fact_key) {
        setFocusStreaks(prev => ({ ...prev, [data.fact_key]: data.fact_streak }));
      }
      if (data.fact_mastered) {
        setFocusMastered(prev => [...prev, data.fact_key]);
        setFactMasteredFlash(true);
        setTimeout(() => setFactMasteredFlash(false), 1500);
      }
      if (data.focus_set_complete && data.new_focus_set) {
        setSetCompleteFlash(true);
        setTimeout(() => {
          setSetCompleteFlash(false);
          setFocusSet(data.new_focus_set);
          setFocusMastered([]);
          setFocusStreaks({});
        }, 1500);
      }

      if (data.is_correct) {
        sounds.correct();
        if (data.total_coins !== undefined) updateUserCoins(data.total_coins);
        const newStreak = data.progress.session_streak;
        if (newStreak > 0 && newStreak % 5 === 0 && newStreak > prevStreak.current) {
          setTimeout(() => sounds.streak(), 200);
        }
        prevStreak.current = newStreak;
      } else {
        sounds.wrong();
        prevStreak.current = 0;
      }

      setPhase("feedback");

      if (data.reward_earned) {
        setBonusMode(data.bonus_mode);
        sounds.reward();
        scheduleNext(() => { setFlashClass(""); setPhase("reward"); }, 1500);
      } else if (data.band_advanced) {
        scheduleNext(() => { setFlashClass(""); setSessionBand(data.new_band); setPhase("preview"); }, 2000);
      } else {
        const delay = data.is_correct ? 1200 : 2500;
        scheduleNext(() => { setFlashClass(""); fetchProblem(); }, delay);
      }
    } catch (err) {
      if (err.response?.status === 409) return recoverIfStuck();
      toast.error("Submit failed");
      scheduleNext(fetchProblem, 300);
    }
  };

  const timerPct = maxTime > 0 ? (timeLeft / maxTime) * 100 : 0;
  const progressPct = (progress.session_correct / progress.target) * 100;
  const masteryPct = mastery.total_facts > 0 ? (mastery.mastered_count / mastery.total_facts) * 100 : 0;
  const timerColor = timeLeft <= 1.5 ? "bg-magenta" : timeLeft <= 3 ? "bg-yellow-500" : "bg-cyber";

  if (phase === "preview") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="skill-preview-phase">
        <SkillPreview bandId={sessionBand ?? 0} onReady={() => { setPhase("loading"); fetchProblem(); }} />
      </div>
    );
  }

  if (phase === "band_complete") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="band-complete-screen">
        <div className="text-center animate-slide-up">
          <Trophy className="w-20 h-20 text-cyber mx-auto mb-6 animate-glow-pulse" />
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-white mb-4 tracking-tighter">
            BAND <span className="neon-text-cyber">MASTERED</span>
          </h1>
          <p className="font-body text-lg text-white/60 mb-6">
            You've mastered all {mastery.total_facts} facts in this level!
          </p>
          <button onClick={() => navigate("/student")} data-testid="back-to-dashboard-btn"
            className="btn-cyber px-10 py-4 rounded-full font-heading text-lg tracking-wider">
            BACK TO DASHBOARD
          </button>
        </div>
      </div>
    );
  }

  const skipGame = async () => {
    try {
      await API.post("/arcade/skip");
      toast.success("Game skipped. Here comes your next round!");
      setFeedback(null);
      setPhase("loading");
      startSession();
    } catch {
      toast.error("Couldn't skip the game. Try again.");
    }
  };

  if (phase === "reward") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="reward-screen">
        <div className="text-center animate-slide-up">
          <Gamepad2 className="w-20 h-20 text-cyber mx-auto mb-6 animate-glow-pulse" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tighter">
            REWARD <span className="neon-text-cyber">UNLOCKED</span>
          </h1>
          <p className="font-body text-lg text-white/60 mb-2">You solved 20 problems!</p>
          {bonusMode && <p className="font-mono text-sm text-cyber animate-glow-pulse mb-6">BONUS MODE ACTIVATED</p>}
          {!bonusMode && <p className="font-mono text-sm text-white/40 mb-6">Standard mode: 60 second session</p>}
          <button onClick={() => navigate("/arcade")} data-testid="launch-game-btn"
            className="btn-cyber px-10 py-4 rounded-full font-heading text-lg tracking-wider">
            LAUNCH GAME
          </button>
          <button onClick={skipGame} data-testid="skip-game-btn"
            className="block mx-auto mt-6 font-body text-sm text-white/40 hover:text-white underline underline-offset-4 transition-colors duration-200">
            Skip game and keep practicing
          </button>
        </div>
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-cyber animate-pulse tracking-widest">LOADING MISSION...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-6 ${flashClass}`} data-testid="math-practice">
      {/* HUD Top Bar */}
      <div className="fixed top-16 left-0 right-0 z-40 px-4 py-3">
        <div className="max-w-2xl mx-auto space-y-2">
          <div className="glass-surface rounded-xl px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="text-center" data-testid="hud-correct">
                <p className="font-mono text-2xl font-bold text-cyber">{progress.session_correct}</p>
                <p className="font-mono text-[10px] text-white/30 uppercase tracking-wider">Correct</p>
              </div>
              <div className="text-center" data-testid="hud-streak">
                <div className="flex items-center gap-1">
                  <Flame className={`w-4 h-4 ${progress.session_streak >= 5 ? "text-magenta" : "text-white/30"}`} />
                  <p className={`font-mono text-2xl font-bold ${progress.session_streak >= 5 ? "text-magenta animate-streak-pop" : "text-white"}`}>
                    {progress.session_streak}
                  </p>
                </div>
                <p className="font-mono text-[10px] text-white/30 uppercase tracking-wider">Streak</p>
              </div>
            </div>
            <div className="w-32">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] text-white/30">ARCADE</span>
                <span className="font-mono text-xs text-cyber">{progress.session_correct}/{progress.target}</span>
              </div>
              <Progress value={progressPct} className="h-1.5 bg-white/5" data-testid="progress-bar" />
            </div>
          </div>

          {/* Mastery bar + Focus set */}
          <div className="glass-surface rounded-xl px-5 py-2">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <Brain className="w-3.5 h-3.5 text-neon" />
                <span className="font-mono text-[10px] text-white/30 uppercase tracking-wider">Facts Mastered</span>
              </div>
              <span className="font-mono text-xs text-neon">{mastery.mastered_count}/{mastery.total_facts}</span>
            </div>
            <Progress value={masteryPct} className="h-1.5 bg-white/5" data-testid="mastery-bar" />
            {/* Focus set dots: each fact = 3 small circles showing streak progress */}
            <div className="flex gap-3 mt-2 justify-center">
              {focusSet.map((fk) => {
                const streak = focusStreaks[fk] || 0;
                const done = focusMastered.includes(fk);
                const active = fk === currentFact;
                return (
                  <div key={fk} className={`flex gap-0.5 ${active ? "scale-125" : ""} transition-transform duration-200`}
                    data-testid={`focus-dot-${fk}`} title={fk}>
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        done ? "bg-neon shadow-[0_0_4px_#39FF14]" : i < streak ? "bg-cyber" : "bg-white/10"
                      }`} />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Fact Mastered Flash */}
      {factMasteredFlash && (
        <div className="fixed top-52 left-0 right-0 z-50 flex justify-center pointer-events-none" data-testid="fact-mastered-flash">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neon/20 border border-neon/30 animate-slide-up">
            <CheckCircle2 className="w-4 h-4 text-neon" />
            <span className="font-heading text-sm font-bold text-neon">FACT MASTERED!</span>
          </div>
        </div>
      )}

      {/* Set Complete Flash */}
      {setCompleteFlash && (
        <div className="fixed top-52 left-0 right-0 z-50 flex justify-center pointer-events-none" data-testid="set-complete-flash">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyber/20 border border-cyber/30 animate-slide-up">
            <Zap className="w-4 h-4 text-cyber" />
            <span className="font-heading text-sm font-bold text-cyber">SET COMPLETE! Loading next 5...</span>
          </div>
        </div>
      )}

      {/* Problem Card */}
      <div className="w-full max-w-lg mt-16">
        <div className="mb-4" data-testid="timer-bar">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-white/30" />
              <span className="font-mono text-xs text-white/30">{problem?.skill_name}</span>
            </div>
            <span className={`font-mono text-sm font-bold ${timeLeft <= 1.5 ? "text-magenta" : "text-white/60"}`}>
              {timeLeft.toFixed(1)}s
            </span>
          </div>
          <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${timerColor} transition-all duration-100`} style={{ width: `${timerPct}%` }} />
          </div>
        </div>

        <div className="tracing-card bg-surface p-10 text-center">
          <p className="font-mono text-5xl lg:text-6xl font-bold text-white tracking-wider mb-8" data-testid="problem-text">
            {problem?.text} = <span className="text-cyber">?</span>
          </p>

          {phase === "feedback" && feedback && (
            <div className="mb-6 animate-slide-up" data-testid="answer-feedback">
              {feedback.is_correct ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-center gap-2">
                    <Zap className="w-6 h-6 text-cyber" />
                    <span className="font-heading text-xl font-bold text-cyber">CORRECT!</span>
                  </div>
                  {feedback.coins_earned > 0 && (
                    <span className="font-mono text-sm text-yellow-400 animate-slide-up" data-testid="coins-earned">
                      +{feedback.coins_earned} coin{feedback.coins_earned > 1 ? "s" : ""}
                    </span>
                  )}
                  {feedback.fact_streak > 0 && (
                    <span className="font-mono text-xs text-white/40">
                      {feedback.fact_streak >= MASTERY_REQUIRED ? "MASTERED" : `${feedback.fact_streak}/${MASTERY_REQUIRED} to master`}
                    </span>
                  )}
                </div>
              ) : (
                <div>
                  <span className="font-heading text-xl font-bold text-magenta block mb-2">
                    {feedback.timeout ? "TIME'S UP!" : "INCORRECT"}
                  </span>
                  <span className="font-mono text-4xl text-white font-bold" data-testid="correct-answer-flash">
                    {feedback.correct_answer}
                  </span>
                  <p className="font-mono text-xs text-white/30 mt-2">Remember this answer!</p>
                </div>
              )}
            </div>
          )}

          {phase === "active" && (
            <form onSubmit={handleSubmit} className="flex items-center gap-3 justify-center">
              <Input
                ref={inputRef}
                type="number"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Your answer"
                data-testid="answer-input"
                className="w-48 h-14 text-center font-mono text-2xl bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-cyber focus:ring-cyber/30"
                autoComplete="off"
              />
              <Button type="submit" data-testid="submit-answer-btn"
                className="btn-cyber h-14 px-6 rounded-lg font-heading tracking-wider">
                <ArrowRight className="w-5 h-5" />
              </Button>
            </form>
          )}
          {phase === "active" && offline && (
            <p className="mt-4 font-mono text-xs text-[#FFE600]" data-testid="timer-paused-note">Timer paused while reconnecting...</p>
          )}
        </div>
      </div>
    </div>
  );
}
